import React, { useCallback, useEffect, useState } from "react";
import {
  IApiKeyConfigurationIndex,
  IApiKeyConfigurationIndexData,
} from "../../../../models/ai/IAI";
import { connect, useDispatch } from "react-redux";
import SweetAlert from "react-bootstrap-sweetalert";
import ReactModal from "react-modal";
import LoaderSpinner from "../../../../common-components/loader";
import { alertConstants, getAlertData } from "../../../../helper/sweet-alert";
import { setApiConfigurations } from "../../../../store/slices/ai/aiConfigurationSlice";
import {
  deleteApiKeyConfiguration,
  getAllApiConfiguration,
  getApiKeyConfiguration,
} from "../../../../action/ai/aiConfigurationActionCreator";
import ApiKeyConfigurationCreate from "./apiKeyConfigurationCreate";
import ApiKeyConfigurationEdit from "./apiKeyConfigurationEdit";
import {
  getColleges,
  getUserTypes,
} from "../../../../action/common/commonActionCreator";
import FloatingLabelDropDown from "../../../../common-components/floatingdropdown";
import { sortAlphabetically } from "../../../../common-functions/commonFunctions";

const ApiKeyConfigurationIndex = React.memo(
  (props: IApiKeyConfigurationIndex) => {
    const {
      getApiKeyConfiguration,
      getApiKeyConfigurations,
      apiKeyConfiguration,
      deleteApiKeyConfiguration,
      apiKeyConfigurations,
      getAllUserTypes,
      getColleges,
      userTypes,
      colleges,
    } = props;

    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [isDelete, setIsDelete] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<number>(0);
    const [showAlert, setShowAlert] = useState<boolean>(false);
    const [alertType, setAlertType] = useState<any>("success");
    const [alertTitle, setAlertTitle] = useState<string>("Default");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isEditLoading, setIsEditLoading] = useState<boolean>(false);
    const [selectedCollegeId, setSelectedCollegeId] = useState<number>();
    const dispatch = useDispatch();

    const handleCreateOpen = () => setShowCreate(true);
    const handleCreateClose = () => setShowCreate(false);
    const handleEditOpen = () => setShowEdit(true);
    const handleEditClose = () => setShowEdit(false);

    const editItem = useCallback(async (id: number) => {
      handleEditOpen();
      setIsEditLoading(true);
      try {
        await getApiKeyConfiguration(id);
      } finally {
        setIsEditLoading(false);
      }
    }, []);

    const deleteItem = useCallback((id: number) => {
      setIsDelete(true);
      setAlert(alertConstants.deleteCheck);
      setItemToDelete(id);
    }, []);

    const onConfirm = useCallback(() => {
      if (alertType === alertConstants.error) {
        setShowAlert(false);
      } else if (isDelete === true) {
        setShowAlert(false);
        setIsDelete(false);
        deleteApiKeyConfiguration(itemToDelete, setAlert);
      } else {
        setShowAlert(false);
        setIsDelete(false);
        getAllApiConfigurations(selectedCollegeId);
      }
    }, [alertType, isDelete, itemToDelete]);

    const setAlert = useCallback((type: string) => {
      let alertData = getAlertData(type);
      if (alertData) {
        setShowAlert(true);
        setAlertType(alertData.type);
        setAlertTitle(alertData.title);
      }
    }, []);

    const getAllApiConfigurations = useCallback(async (collegeId?: number) => {
      setIsLoading(true);
      try {
        await getApiKeyConfigurations(collegeId);
      } finally {
        setIsLoading(false);
      }
    }, []);

    useEffect(() => {
      getAllApiConfigurations();
      getColleges();
      getAllUserTypes();
      return () => {
        dispatch(setApiConfigurations([]));
      };
    }, []);

    const onFilterChange = useCallback((e: any) => {
      if (e.target.name == "college") {
        setSelectedCollegeId(e.target.value);
        getAllApiConfigurations(e.target.value);
      }
    }, []);

    return (
      <div className="admin-wrap">
        <div className="admincontent">
          <div className="title">
            <h2>AI Agent Configuration</h2>
            <div className="side-filter">
              <div className="justify-content-left mw-250">
                <FloatingLabelDropDown
                  label=""
                  placeholder="-Select University Management"
                  onChange={(e: any) => onFilterChange(e)}
                  isMandatory={false}
                  value={selectedCollegeId}
                  name="college"
                  optionsList={sortAlphabetically(colleges)}
                />
              </div>
              <a
                href="javascript:void(0)"
                className="btn btn-primary"
                data-bs-toggle="modal"
                data-bs-target="#addnewbuttons"
                onClick={() => {
                  handleCreateOpen();
                }}
              >
                <img
                  src="assets/images/icon/plus.svg"
                  alt=""
                  className="me-2"
                />
                Add New
              </a>
            </div>
          </div>

          <ul className="d-flex gap-3 flex-wrap mt-28">
            {isLoading ? (
              <LoaderSpinner />
            ) : apiKeyConfigurations && apiKeyConfigurations?.length > 0 ? (
              apiKeyConfigurations.map(
                (configuration: IApiKeyConfigurationIndexData) => {
                  return (
                    <li className="admin-card">
                      <div className="top">
                        <div className="btn-flex">
                          <button
                            className="btn-edit"
                            onClick={() => editItem(configuration.id)}
                          >
                            <img src="/assets/images/icon/edit-wh.svg" alt="" />
                          </button>
                          <button
                            className="btn-delete"
                            onClick={() => deleteItem(configuration.id)}
                          >
                            <img
                              src="/assets/images/icon/trash-wh.svg"
                              alt=""
                            />
                          </button>
                        </div>
                      </div>
                      <div className="btm">
                        <h2>
                          {
                            colleges?.find(
                              (c) => c.id === configuration.collegeId
                            )?.name
                          }
                        </h2>
                        <h3>
                          {
                            userTypes?.find(
                              (u) => u.id === configuration.userType
                            )?.name
                          }
                        </h3>
                      </div>
                    </li>
                  );
                }
              )
            ) : (
              <p>No Records Found</p>
            )}
          </ul>
        </div>
        <ReactModal
          isOpen={showCreate}
          onRequestClose={handleCreateClose}
          className="react-modal-content"
          overlayClassName="react-modal-overlay"
          shouldCloseOnOverlayClick={false}
        >
          <ApiKeyConfigurationCreate
            setShowCreate={handleCreateClose}
            getAllConfigurations={getApiKeyConfigurations}
          />
        </ReactModal>

        {isEditLoading ? (
          <LoaderSpinner />
        ) : (
          <ReactModal
            isOpen={showEdit}
            onRequestClose={handleEditClose}
            className="react-modal-content"
            overlayClassName="react-modal-overlay"
            shouldCloseOnOverlayClick={false}
          >
            <ApiKeyConfigurationEdit
              setShowEdit={handleEditClose}
              getAllConfigurations={getApiKeyConfigurations}
              aiApiKeyConfiguration={apiKeyConfiguration}
            />
          </ReactModal>
        )}
        <SweetAlert
          show={showAlert}
          success={false}
          type={alertType}
          title={alertTitle}
          onConfirm={() => onConfirm()}
          showCancel={isDelete}
          onCancel={() => {
            setShowAlert(false);
            setIsDelete(false);
          }}
          cancelBtnText={"No"}
          confirmBtnText={isDelete ? "Yes" : "Ok"}
        />
      </div>
    );
  }
);

const mapDispatchToProps = {
  getApiKeyConfigurations: (collegeId?: number) =>
    getAllApiConfiguration(collegeId),
  getApiKeyConfiguration: (id: number) => getApiKeyConfiguration(id),
  deleteApiKeyConfiguration: (id: number, callBack: Function) =>
    deleteApiKeyConfiguration(id, callBack),
  getColleges: () => getColleges(),
  getAllUserTypes: () => getUserTypes(),
};

function mapStateToProps(state: any) {
  return {
    apiKeyConfigurations: state.aiConfiguration?.apiKeyConfigurations,
    apiKeyConfiguration: state.aiConfiguration?.apiKeyConfiguration,
    colleges: state.common.colleges,
    userTypes: state.common.userTypes,
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ApiKeyConfigurationIndex);
